// src/components/common/Alert.jsx
import React, { useEffect } from 'react';

const Alert = ({ type, message, onClose, autoClose = true, duration = 5000 }) => {
  useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, duration, onClose]);

  // Styles object for the alert component
  const styles = {
    container: {
      marginBottom: '1rem',
      padding: '1rem',
      borderRadius: '0.375rem',
      borderLeftWidth: '4px',
      position: 'relative',
      animation: 'slideIn 0.3s ease-out',
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    messageWrapper: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
    icon: {
      marginRight: '0.75rem',
      fontSize: '1.25rem',
      fontWeight: 'bold',
    },
    message: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.25rem',
      cursor: 'pointer',
      padding: '0 0.5rem',
      marginLeft: '0.5rem',
      transition: 'opacity 0.2s',
    },
    closeButtonHover: {
      opacity: 0.75,
    },
  };

  // Type-specific styles
  const typeStyles = {
    success: {
      container: {
        backgroundColor: '#f0fdf4',
        borderColor: '#4ade80',
      },
      icon: {
        color: '#166534',
      },
      message: {
        color: '#166534',
      },
      closeButton: {
        color: '#166534',
      },
    },
    error: {
      container: {
        backgroundColor: '#fef2f2',
        borderColor: '#f87171',
      },
      icon: {
        color: '#991b1b',
      },
      message: {
        color: '#991b1b',
      },
      closeButton: {
        color: '#991b1b',
      },
    },
    warning: {
      container: {
        backgroundColor: '#fefce8',
        borderColor: '#facc15',
      },
      icon: {
        color: '#854d0e',
      },
      message: {
        color: '#854d0e',
      },
      closeButton: {
        color: '#854d0e',
      },
    },
    info: {
      container: {
        backgroundColor: '#eff6ff',
        borderColor: '#60a5fa',
      },
      icon: {
        color: '#1e40af',
      },
      message: {
        color: '#1e40af',
      },
      closeButton: {
        color: '#1e40af',
      },
    },
  };

  // Icon mapping
  const icons = {
    success: '✓',
    error: '✗',
    warning: '⚠',
    info: 'ℹ',
  };

  const currentType = typeStyles[type] || typeStyles.info;
  const icon = icons[type] || icons.info;

  // Merge styles
  const containerStyle = {
    ...styles.container,
    ...currentType.container,
  };

  const iconStyle = {
    ...styles.icon,
    ...currentType.icon,
  };

  const messageStyle = {
    ...styles.message,
    ...currentType.message,
  };

  const closeButtonStyle = {
    ...styles.closeButton,
    ...currentType.closeButton,
  };

  return (
    <div style={containerStyle} role="alert">
      <div style={styles.content}>
        <div style={styles.messageWrapper}>
          <span style={iconStyle}>{icon}</span>
          <span style={messageStyle}>{message}</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={closeButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.75';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            aria-label="Close alert"
          >
            ×
          </button>
        )}
      </div>

      {/* Add keyframe animations */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOut {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-100%);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

// Optional: Create a separate CSS file for global styles
// If you prefer external CSS, you can create an Alert.css file:
/*
.alert-slide-in {
  animation: slideIn 0.3s ease-out;
}

.alert-slide-out {
  animation: slideOut 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
}
*/

export default Alert;